/**
 * popoutShimState — module singleton holding the latest snapshots
 * received via BroadcastChannel in the popout window. The hooks
 * (useNetobsTraffic etc.) branch on isPopoutMode() and read from here
 * instead of from `window.nm` / Colyseus.
 *
 * Subscribe is wired once per window — see NetobsPopoutView, which
 * mounts the listener on app boot when in popout mode.
 *
 * State is published as the same shape the live hooks expect, so the
 * existing panel components don't need to know about the popout
 * channel at all.
 */

import { subscribeNetobsFrames, type NetobsFrame, type MessageTypeStatsRecord } from './popoutChannel';

interface ShimTrafficEvent {
  seq: number;
  timestamp: string;
  direction: 'client->server' | 'server->client';
  type: string;
  bytes: number;
}

interface ShimState {
  tick: {
    meanMs: number;
    maxMs: number;
    sampleCount: number;
    budgetMs: number;
    timestamp: number;
    receivedAt: number;
  } | null;
  traffic: {
    recentTraffic: ShimTrafficEvent[];
    outboundStats: Map<string, MessageTypeStatsRecord>;
    inboundStats: Map<string, MessageTypeStatsRecord>;
    receivedAt: number;
  } | null;
  latency: {
    snaps: Array<{
      sid: string;
      name?: string;
      phase1Ms: number;
      phase2Ms: number;
      rttMs: number;
      clockReady: boolean;
      updatedAt: number;
    }>;
    timestamp: number;
    receivedAt: number;
  } | null;
}

const state: ShimState = {
  tick: null,
  traffic: null,
  latency: null,
};

let listenersWired = false;

/** Mount the BroadcastChannel listener that updates this state.
 *  Call once at popout-app boot. Idempotent — safe to call multiple times. */
export function mountPopoutShim(): () => void {
  if (listenersWired) return () => {};
  listenersWired = true;
  const off = subscribeNetobsFrames((frame: NetobsFrame) => {
    const now = Date.now();
    if (frame.type === 'tick') {
      state.tick = { ...frame.payload, receivedAt: now };
    } else if (frame.type === 'traffic') {
      state.traffic = {
        recentTraffic: frame.payload.recentTraffic,
        outboundStats: new Map(frame.payload.outboundStats),
        inboundStats: new Map(frame.payload.inboundStats),
        receivedAt: now,
      };
    } else if (frame.type === 'latency') {
      state.latency = {
        snaps: frame.payload.snaps,
        timestamp: frame.payload.timestamp,
        receivedAt: now,
      };
    }
  });
  return () => {
    off();
    listenersWired = false;
  };
}

export const popoutShim = {
  getTick: () => state.tick,
  getTraffic: () => state.traffic,
  getLatency: () => state.latency,
};
